import type { FreeformSendBlockedReason, PlatformTypeName } from "@multi-chat/shared";

export type ComposerMode =
  | "whatsapp_freeform"
  | "whatsapp_window_closed"
  | "facebook_freeform"
  | "facebook_window_closed"
  | "instagram_freeform"
  | "instagram_window_closed"
  | "unsupported_channel";

export function isCustomerServiceWindowOpen(expiresAt: string | null, nowMs: number): boolean {
  if (!expiresAt) return false;
  const parsed = Date.parse(expiresAt);
  return !Number.isNaN(parsed) && parsed > nowMs;
}

/**
 * Presentation mode for the conversation composer.
 * WhatsApp free-form uses WhatsApp CS-window expiry.
 * Facebook / Instagram free-form use backend-authoritative `canSendFreeformText`.
 */
export function deriveComposerMode(input: {
  platformType: PlatformTypeName;
  customerServiceWindowExpiresAt: string | null;
  canSendFreeformText: boolean;
  freeformSendBlockedReason: FreeformSendBlockedReason | null;
  nowMs: number;
}): ComposerMode {
  switch (input.platformType) {
    case "WHATSAPP":
      return isCustomerServiceWindowOpen(input.customerServiceWindowExpiresAt, input.nowMs)
        ? "whatsapp_freeform"
        : "whatsapp_window_closed";
    case "FACEBOOK":
      if (input.canSendFreeformText) {
        return "facebook_freeform";
      }
      return input.freeformSendBlockedReason === "WINDOW_CLOSED"
        ? "facebook_window_closed"
        : "unsupported_channel";
    case "INSTAGRAM":
      if (input.canSendFreeformText) {
        return "instagram_freeform";
      }
      return input.freeformSendBlockedReason === "WINDOW_CLOSED"
        ? "instagram_window_closed"
        : "unsupported_channel";
    default: {
      const _exhaustive: never = input.platformType;
      return _exhaustive;
    }
  }
}

/** Template action requires both conversation identity and org WhatsApp connection. */
export function canShowWhatsAppTemplateAction(
  platformType: PlatformTypeName,
  orgCanSendTemplate: boolean,
): boolean {
  return platformType === "WHATSAPP" && orgCanSendTemplate;
}

export interface ConversationComposerPresentation {
  mode: ComposerMode;
  showTemplateAction: boolean;
  showFreeformComposer: boolean;
  showClosedWindowPrompt: boolean;
}

/**
 * Details drive the composer only when they belong to the selected conversation.
 * A cleared or stale payload is unknown: callers must not treat it as closed or disabled.
 */
export function selectedComposerDetails<T extends { conversationId: number }>(
  selectedConversationId: number | null,
  details: T | null,
): T | null {
  if (selectedConversationId == null || details == null) return null;
  if (details.conversationId !== selectedConversationId) return null;
  return details;
}

/** Combined composer chrome for WhatsApp template vs free-form vs unsupported channels. */
export function conversationComposerPresentation(input: {
  platformType: PlatformTypeName;
  customerServiceWindowExpiresAt: string | null;
  canSendFreeformText: boolean;
  freeformSendBlockedReason: FreeformSendBlockedReason | null;
  nowMs: number;
  orgCanSendTemplate: boolean;
}): ConversationComposerPresentation {
  const mode = deriveComposerMode({
    platformType: input.platformType,
    customerServiceWindowExpiresAt: input.customerServiceWindowExpiresAt,
    canSendFreeformText: input.canSendFreeformText,
    freeformSendBlockedReason: input.freeformSendBlockedReason,
    nowMs: input.nowMs,
  });
  return {
    mode,
    showTemplateAction: canShowWhatsAppTemplateAction(
      input.platformType,
      input.orgCanSendTemplate,
    ),
    showFreeformComposer:
      mode === "whatsapp_freeform" ||
      mode === "facebook_freeform" ||
      mode === "instagram_freeform",
    showClosedWindowPrompt:
      mode === "whatsapp_window_closed" ||
      mode === "facebook_window_closed" ||
      mode === "instagram_window_closed",
  };
}
